import type { Lot } from '../types/lot'

// v2.2 findings.md #67 / #71: report rows used to expose only the lot's
// *name* (`lot`). Lot names are unique per production unit, not per farm, so
// a pivot "by Lot" merged "Lot 1" in A3, A4, A5 and A6 into one series, and a
// `sumOfLast` stock keyed on the name kept one tank per week. These columns
// are added to every lot-scoped dataset so the user can break down by unit
// and by an unambiguous lot label, and group a stocked batch by its lineage:
//
// - farmName / productionUnitName — display names of the row's location.
// - lotUnit — "Lot 1 · A3": the lot name plus its unit (plus the facility
//   when two facilities share that unit name). One label per lot.
// - lotOrigin — the lineage root's name plus where the root lives. Lots
//   split from one batch share it; independently created same-name lots
//   don't (each is its own root), so nothing is merged that isn't linked.

export const LOCATION_COLUMN_KEYS = ['farmName', 'productionUnitName', 'lotUnit', 'lotOrigin'] as const

export type LocationFarm = {
  id: string
  name?: string
  productionUnits?: Array<{ id: string; name?: string }>
}

export type LocationLot = Pick<Lot, 'id' | 'lotKey' | 'farmId' | 'productionUnitId' | 'lineageRootId' | 'sourceLotKey'>

type Row = Record<string, unknown>

const str = (value: unknown): string => (typeof value === 'string' ? value.trim() : '')
const norm = (value: unknown): string => str(value).toLowerCase()

export const buildLocationIndex = (farms: readonly LocationFarm[], lots: readonly LocationLot[]) => {
  const farmNameById = new Map<string, string>()
  const unitById = new Map<string, { name: string; farmId: string }>()
  const unitNameCount = new Map<string, number>()
  for (const farm of farms) {
    const farmId = str(farm.id)
    if (!farmId) continue
    farmNameById.set(farmId, str(farm.name) || farmId)
    for (const unit of farm.productionUnits ?? []) {
      const unitId = str(unit.id)
      if (!unitId) continue
      const name = str(unit.name) || unitId
      unitById.set(unitId, { name, farmId })
      unitNameCount.set(norm(name), (unitNameCount.get(norm(name)) ?? 0) + 1)
    }
  }

  const lotsById = new Map<string, LocationLot>()
  const lotIdByUnitAndKey = new Map<string, string>()
  for (const lot of lots) {
    const id = str(lot.id)
    if (!id) continue
    lotsById.set(id, lot)
    const unitId = str(lot.productionUnitId)
    if (unitId && str(lot.lotKey)) lotIdByUnitAndKey.set(`${unitId}::${norm(lot.lotKey)}`, id)
  }

  /** Unit label, prefixed with its facility only when the unit name isn't unique across facilities. */
  const unitLabel = (unitId: string): string => {
    const unit = unitById.get(unitId)
    if (!unit) return ''
    if ((unitNameCount.get(norm(unit.name)) ?? 0) <= 1) return unit.name
    return `${farmNameById.get(unit.farmId) ?? unit.farmId} / ${unit.name}`
  }

  return { farmNameById, unitById, lotsById, lotIdByUnitAndKey, unitLabel }
}

export type LocationIndex = ReturnType<typeof buildLocationIndex>

const resolveRowLot = (row: Row, index: LocationIndex): LocationLot | undefined => {
  const explicit = str(row.lotId)
  if (explicit && index.lotsById.has(explicit)) return index.lotsById.get(explicit)
  // `lots` dataset rows are the lots themselves.
  const ownId = str(row.id)
  if (ownId && str(row.lotKey) && index.lotsById.has(ownId)) return index.lotsById.get(ownId)
  const unitId = str(row.productionUnitId)
  const label = norm(row.lot) || norm(row.lotKey)
  if (!unitId || !label) return undefined
  const matchId = index.lotIdByUnitAndKey.get(`${unitId}::${label}`)
  return matchId ? index.lotsById.get(matchId) : undefined
}

const placeLabel = (lot: LocationLot, index: LocationIndex): string => {
  const unitId = str(lot.productionUnitId)
  if (unitId) return index.unitLabel(unitId) || unitId
  const farmId = str(lot.farmId)
  return farmId ? (index.farmNameById.get(farmId) ?? farmId) : ''
}

const withPlace = (name: string, place: string): string => (place ? `${name} · ${place}` : name)

export const decorateRowWithLocation = (row: Row, index: LocationIndex): Row => {
  const lot = resolveRowLot(row, index)
  const farmId = str(row.farmId) || str(lot?.farmId)
  const unitId = str(row.productionUnitId) || str(lot?.productionUnitId)
  const lotName = str(lot?.lotKey) || str(row.lot) || str(row.lotKey)

  let lotOrigin = ''
  if (lot) {
    const root = index.lotsById.get(str(lot.lineageRootId)) ?? lot
    lotOrigin = withPlace(str(root.lotKey) || str(lot.sourceLotKey) || lotName, placeLabel(root, index))
  }

  return {
    ...row,
    farmName: farmId ? (index.farmNameById.get(farmId) ?? '') : '',
    productionUnitName: unitId ? (index.unitById.get(unitId)?.name ?? '') : '',
    lotUnit: lotName ? withPlace(lotName, unitId ? index.unitLabel(unitId) : '') : '',
    lotOrigin
  }
}

export const decorateRowsWithLocation = (rows: readonly Row[], index: LocationIndex): Row[] =>
  rows.map((row) => decorateRowWithLocation(row, index))

/**
 * Labels of lots that share a name but are different lots (different
 * units/facilities) among these rows — used to warn when a report or widget
 * groups by the plain `lot` name. Empty when nothing is merged.
 */
export const findMergedLotNames = (rows: readonly Row[]): Array<{ lot: string; lotUnits: string[] }> => {
  const unitsByName = new Map<string, { lot: string; lotUnits: Set<string> }>()
  for (const row of rows) {
    const lot = str(row.lot) || str(row.lotKey)
    const lotUnit = str(row.lotUnit)
    if (!lot || !lotUnit) continue
    const entry = unitsByName.get(norm(lot)) ?? { lot, lotUnits: new Set<string>() }
    entry.lotUnits.add(lotUnit)
    unitsByName.set(norm(lot), entry)
  }
  return Array.from(unitsByName.values())
    .filter((entry) => entry.lotUnits.size > 1)
    .map((entry) => ({ lot: entry.lot, lotUnits: Array.from(entry.lotUnits).sort() }))
}
